/**
 * COPSOQ III — Partie 1 : questions sociodémographiques et professionnelles.
 *
 * Non scorée : ces réponses décrivent le répondant (`donnees.profil`) et ne
 * participent à aucune échelle COPSOQ. Découpée en écrans thématiques.
 */

import {
  OUI_NON,
  type EcranQuestions,
  type QuestionEcran,
} from "./copsoq3-ecrans";

const RUBRIQUE_SPV = "Sapeur-pompier volontaire";
const RUBRIQUE_SPP = "Sapeur-pompier professionnel";
const RUBRIQUE_SPM = "Sapeur-pompier militaire";
const RUBRIQUE_CIS = "Centre de secours (CIS)";
const RUBRIQUE_EM = "État-major";

/** Questions communes au volontaire et au professionnel, posées une fois par
 * statut coché : un double statut peut s'exercer dans deux SDIS différents. */
function questionsSdis(
  prefixe: string,
  rubrique: string,
  statut: string,
  sigle: string,
): QuestionEcran[] {
  const condition = { question: "statut", valeur: statut };
  return [
    {
      name: `${prefixe}_sdis`,
      rubrique,
      condition,
      question:
        "Dans quel SDIS exercez-vous votre activité ? Tapez le numéro du département",
      type: "departement",
    },
    {
      name: `${prefixe}_categorie_sdis`,
      rubrique,
      condition,
      question: "De quelle catégorie votre SDIS relève-t-il ?",
      type: "nombre",
      min: 1,
      max: 99,
    },
    {
      name: `${prefixe}_groupement`,
      rubrique,
      condition,
      question: "De quel groupement territorial dépendez-vous ?",
      type: "texte",
    },
    {
      name: `${prefixe}_anciennete`,
      rubrique,
      condition,
      question: `Quel est votre nombre d'années d'activité de ${sigle} ?`,
      type: "nombre",
      min: 0,
      max: 60,
    },
  ];
}

export const copsoq3EcransPartie1: readonly EcranQuestions[] = [
  {
    titre: "Vous et votre statut",
    questions: [
      {
        name: "sexe",
        question: "Sexe",
        type: "choix",
        options: ["Femme", "Homme"],
      },
      { name: "age", question: "Âge", type: "nombre", min: 16, max: 80 },
      {
        name: "statut",
        question:
          "Quel est votre statut actuel ? (plusieurs réponses possibles)",
        type: "choix-multiple",
        options: ["Volontaire", "Professionnel", "Militaire"],
      },
      ...questionsSdis("spv", RUBRIQUE_SPV, "Volontaire", "SPV"),
      ...questionsSdis("spp", RUBRIQUE_SPP, "Professionnel", "SPP"),
      {
        name: "spp_regime",
        rubrique: RUBRIQUE_SPP,
        condition: { question: "statut", valeur: "Professionnel" },
        question: "Quel est votre régime de travail ?",
        type: "choix",
        options: ["Garde", "À la semaine"],
      },
      {
        name: "spm_lieu",
        rubrique: RUBRIQUE_SPM,
        condition: { question: "statut", valeur: "Militaire" },
        question: "Où exercez-vous votre activité ?",
        type: "choix",
        options: ["BSPP", "BMPM"],
      },
      {
        name: "spm_anciennete",
        rubrique: RUBRIQUE_SPM,
        condition: { question: "statut", valeur: "Militaire" },
        question: "Quel est votre nombre d'années d'activité de SP militaire ?",
        type: "nombre",
        min: 0,
        max: 60,
      },
    ],
  },
  {
    titre: "Parcours et grade",
    questions: [
      {
        name: "ancien_bspp_bmpm",
        question: "Avez-vous été pompier à la BSPP ou au BMPM ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "grade",
        question: "Quelle est votre plage de grade ?",
        type: "choix",
        options: [
          "Homme du rang",
          "Sous-officier",
          "Officier",
          "Officier supérieur",
        ],
      },
    ],
  },
  {
    titre: "Affectation",
    questions: [
      {
        name: "affectation",
        question: "Quelle est votre affectation ?",
        type: "choix",
        options: [
          "CIS (centre de secours)",
          "État-major (administration du SDIS)",
        ],
      },
      {
        name: "cis_zone",
        rubrique: RUBRIQUE_CIS,
        condition: {
          question: "affectation",
          valeur: "CIS (centre de secours)",
        },
        question: "Vos interventions sont-elles principalement en zone :",
        type: "choix",
        options: ["Urbaine", "Rurale", "Mixte"],
      },
      {
        name: "cis_officier_garde",
        rubrique: RUBRIQUE_CIS,
        condition: {
          question: "affectation",
          valeur: "CIS (centre de secours)",
        },
        question: "Êtes-vous officier de garde ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "cis_chef_centre",
        rubrique: RUBRIQUE_CIS,
        condition: {
          question: "affectation",
          valeur: "CIS (centre de secours)",
        },
        question: "Êtes-vous chef de centre ou adjoint au chef de centre ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "em_activite",
        rubrique: RUBRIQUE_EM,
        condition: {
          question: "affectation",
          valeur: "État-major (administration du SDIS)",
        },
        question: "Quelle est votre activité ? (plusieurs réponses possibles)",
        type: "choix-multiple",
        options: [
          "Direction",
          "CTA-CODIS",
          "Prévention",
          "Prévision",
          "Opération",
          "Santé",
          "Formation",
          "Technique",
          "RH, finances, pilotage, gestion des territoires",
        ],
      },
    ],
  },
  {
    titre: "Expérience",
    questions: [
      {
        name: "unite_specialisee",
        question: "Appartenez-vous à une unité spécialisée ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "temoin_accident",
        question:
          "Avez-vous déjà été témoin de l'accident grave de l'un de vos collègues ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "syndrome_post_garde",
        question: "Avez-vous déjà entendu parler du syndrome post-garde ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "syndrome_post_garde_symptomes",
        condition: { question: "syndrome_post_garde", valeur: "Oui" },
        question: "Pensez-vous en avoir déjà eu des symptômes ?",
        type: "choix",
        options: OUI_NON,
      },
      {
        name: "syndrome_post_garde_detail",
        condition: { question: "syndrome_post_garde_symptomes", valeur: "Oui" },
        question: "Si oui, lequel(s) ?",
        type: "texte",
        multiligne: true,
        facultative: true,
      },
    ],
  },
];
